import { FULL_NAV_LEVEL, SUPER_ADMIN_LEVEL, userLevelAtLeast } from '../constants/access-levels';
import { KNOWN_MENU_PATHS } from '../classes/routes';

/**
 * Menu paths visible when the user has another permission (avoids DB migrations for every new screen).
 * Keys: route path as in ROUTES / sidebar. Values: any of these in access_permissions grants the key path.
 */
const IMPLIED_MENU_PATHS: Record<string, readonly string[]> = {
	/**
	 * Moov uses /metrc/picking instead of /orders/pick (standard picking is hidden for moov).
	 * Grant metrc picking routes to any user who has the standard picking permission so
	 * WAREHOUSE-type groups don't need a DB migration every time a new metrc picking path is added.
	 * /metrc/pick is the prefix for dynamic order-pick routes (/metrc/pick/:orderno).
	 */
	'/metrc/picking': ['/orders/pick'],
	'/metrc/pick':    ['/orders/pick', '/metrc/picking'],
	'/metrc/receiving': ['/warehouse/receive'],
	'/metrc/receive':   ['/warehouse/receive', '/metrc/receiving'],
	/** Case Break (pre-split) is a warehouse task done by the same pick/receive staff —
	 *  grant it to either standard permission (or the metrc picking/receiving routes)
	 *  so WAREHOUSE groups see it without a DB migration. */
	'/metrc/presplit': ['/orders/pick', '/warehouse/receive', '/metrc/picking', '/metrc/receiving'],
	'/system/status-maintenance': ['/system/types'],
	/** Sales area / territory maintenance: visible to any group that already has shipping or types setup (no separate DB row). */
	'/system/sales-areas': ['/system/shipvia', '/system/types'],
	'/customers/commission-sales': ['/customers/crpt'],
	/**
	 * Customer Payments reads A/R receipts and their allocations, so it rides either
	 * an existing customer-reports grant or any A/R desk grant — without an entry
	 * here the path is in KNOWN_MENU_PATHS, which disables the prefix fallback, and
	 * only full-nav admins would ever see it.
	 */
	'/customers/customer-payments': [
		'/customers/crpt',
		'/customers/cdet',
		'/receivables/dashboard',
		'/receivables/payment-entry',
		'/receivables/payment-report',
		'/receivables/allocations',
	],
	'/customers/salesman-territory': ['/customers/crpt'],
	/**
	 * Open Payables is the A/P aging read document by document — the same supptrans
	 * rows the vendor dashboard, the payment run and Vendor Lookup already show, so
	 * it rides any A/P desk grant rather than needing a DB row per user. Without an
	 * entry here the path is in KNOWN_MENU_PATHS, which disables the prefix fallback,
	 * and only full-nav admins would ever see it.
	 */
	'/vendors/open-payables': [
		'/vendors',
		'/vendors-dashboard',
		'/vendors-check-print',
		'/vendors-pending-payment-runs',
		'/vendor-allocations',
		'/vendors/payment-report',
		'/vendors/daily-transactions',
	],
	'/sales-manager-reports/commission-sales': ['/sales-manager-reports/crpt'],
	'/sales-manager-reports/salesman-territory': ['/sales-manager-reports/crpt'],
	/**
	 * Recurring Replenishment is the reorder worklist for the same reps who already
	 * work the customer-sales and route-builder screens — ride on either grant so a
	 * DB row per user is not needed. Without an entry here the path is in
	 * KNOWN_MENU_PATHS, which disables the prefix fallback, so only full-nav admins
	 * would ever see it.
	 */
	'/sales-manager-reports/recurring-replenishment': [
		'/sales-manager-reports/crpt',
		'/sales-manager-reports/sales-route-builder',
	],
	/** Customers-menu SRB mirrors Salesman Reports grant. */
	'/customers/sales-route-builder': ['/sales-manager-reports/sales-route-builder'],
	/** Live route runner rides on Sales Route Builder access (reps launch it from "Start route"). */
	'/customers/sales-route-runner': ['/customers/sales-route-builder', '/sales-manager-reports/sales-route-builder'],
	/** Salon Prospects: co-located with the route builder — no separate DB row. */
	'/customers/salon-prospects': ['/customers/sales-route-builder', '/sales-manager-reports/sales-route-builder'],
	/** Batch merged invoice/credit PDFs: visible with any typical Orders leaf permission (no separate DB row). */
	'/orders/batch-invoice-print': [
		'/orders/order-inquiry',
		'/orders/openorders',
		'/orders/order-item-inquiry',
		'/orders/dashboard',
		'/orders/dynamic',
		'/orders/entry',
		'/orders/openquotes',
		'/orders/canceledorders',
		'/orders/order-tracking',
		'/orders/create-credit-note',
	],
	/** LeafLink / Distru analytics & queue UI — same grant pattern as batch invoice (no separate DB row). */
	'/orders/channel-reports': [
		'/orders/order-inquiry',
		'/orders/openorders',
		'/orders/order-item-inquiry',
		'/orders/dashboard',
		'/orders/dynamic',
		'/orders/entry',
		'/orders/openquotes',
		'/orders/canceledorders',
		'/orders/order-tracking',
		'/orders/create-credit-note',
	],
	/**
	 * Promo & Intro Performance sits in the Orders reports submenu and reads the
	 * sales ledger, so it rides the same Orders leaf grants as the other two —
	 * without an entry here the path is in KNOWN_MENU_PATHS, which disables the
	 * prefix fallback, and only full-nav admins would ever see it.
	 */
	'/orders/promo-intro': [
		'/orders/order-inquiry',
		'/orders/openorders',
		'/orders/order-item-inquiry',
		'/orders/dashboard',
		'/orders/dynamic',
		'/orders/entry',
		'/orders/openquotes',
		'/orders/canceledorders',
		'/orders/order-tracking',
		'/orders/create-credit-note',
	],
	'/orders/channel-orders': [
		'/orders/order-inquiry',
		'/orders/openorders',
		'/orders/order-item-inquiry',
		'/orders/dashboard',
		'/orders/dynamic',
		'/orders/entry',
		'/orders/openquotes',
		'/orders/canceledorders',
		'/orders/order-tracking',
		'/orders/create-credit-note',
	],
	'/orders/email-intake': [
		'/orders/order-inquiry',
		'/orders/openorders',
		'/orders/order-item-inquiry',
		'/orders/dashboard',
		'/orders/dynamic',
		'/orders/entry',
		'/orders/openquotes',
		'/orders/canceledorders',
		'/orders/order-tracking',
		'/orders/create-credit-note',
	],
	/** RB2: same dispatch planners who have legacy route builder. */
	'/route-builder-v2': ['/dispatches/route-builder'],
	/** Same intake screen mirrored under the Vendors menu — grant it to anyone with the Receivables entry. */
	'/vendors/vendor-intake': ['/receivables/vendor-intake'],
	/** RMA Log report: visible to anyone who already works RMAs (Open RMAs or Create RMA), no separate DB row. */
	'/credits/rma-log': ['/credits/open-credits', '/orders/create-credit-note'],
};

/**
 * Customers/Salesman Reports entries that used bypassReportCatalog but must NOT show to every
 * user with `/customers` (e.g. store POS). Callers combine this with the legacy Sales Route Builder
 * reports_access grant and explicit/implied menu-path grants.
 */
export const PATH_GATED_BYPASS_REPORTS: ReadonlySet<string> = new Set([
	'/customers/sales-route-builder',
	'/customers/sales-route-runner',
	'/customers/salon-prospects',
	'/sales-manager-reports/sales-route-builder',
]);

export function isPathGatedBypassReport(path: string | null | undefined): boolean {
	return !!path && PATH_GATED_BYPASS_REPORTS.has(path);
}

/**
 * Collapsible parent: show "General Ledger" when the user has any child route under it (many DBs only store leaf paths).
 */
function impliedGeneralLedgerParent(path: string, access_permissions: string[]): boolean {
	if (path !== '/general-ledger') {
		return false;
	}
	return access_permissions.some((p) => typeof p === 'string' && p.startsWith('/general-ledger/'));
}

/**
 * New GL screens: show without a DB migration for every user group if they already have any other General Ledger route.
 */
const IMPLIED_NEW_GL_SCREENS: readonly string[] = [
	'/general-ledger/gl-impact',
];
function impliedGeneralLedgerGlImpact(path: string, access_permissions: string[]): boolean {
	if (!IMPLIED_NEW_GL_SCREENS.includes(path)) {
		return false;
	}
	return access_permissions.some(
		(p) =>
			typeof p === 'string' &&
			p.startsWith('/general-ledger/') &&
			!IMPLIED_NEW_GL_SCREENS.includes(p)
	);
}

/** Submenu under General Ledger → Maintenance (chart, posting setup, close GL, etc.). */
const GENERAL_LEDGER_MAINTENANCE_LEAF_PATHS: readonly string[] = [
	'/system/gl-accounts/account-sections',
	'/system/gl-accounts/account-groups',
	'/system/gl-accounts',
	'/system/bank-accounts',
	'/general-ledger/budgets',
	'/general-ledger/tags',
	'/general-ledger/general-posting-setup',
	'/general-ledger/sales-gl-postings',
	'/general-ledger/cogs-gl-postings',
	'/general-ledger/inventory-gl-postings',
	'/general-ledger/gl-close-control',
	'/general-ledger/department-required',
];

/**
 * Legacy user groups: the old "Maintenance" checkbox only stored `/general-ledger` (same path as the GL parent),
 * not these leaf routes. Grant maintenance screens only when there is no real `/general-ledger/...` permission
 * (so we do not broaden groups that already use explicit GL leaf paths).
 */
function impliedGeneralLedgerMaintenanceLeaves(path: string, access_permissions: string[]): boolean {
	if (!GENERAL_LEDGER_MAINTENANCE_LEAF_PATHS.includes(path)) {
		return false;
	}
	if (!access_permissions.includes('/general-ledger')) {
		return false;
	}
	const hasAnyGlSubpath = access_permissions.some(
		(p) => typeof p === 'string' && p.startsWith('/general-ledger/')
	);
	if (hasAnyGlSubpath) {
		return false;
	}
	return true;
}

/**
 * Moov B2B / brand-portal logins use the same admin shell but are scoped to `salesman_manufacturer` rows.
 * They must not inherit distributor-only areas (Metrc, etc.) even if `fullaccess` is mis-set to manager level.
 */
const PORTAL_SCOPED_DENIED_PREFIXES: readonly string[] = ['/metrc'];

function pathMatchesAnyPrefix(path: string, prefixes: readonly string[]): boolean {
	const p = (path || '').split('?')[0] || '';
	return prefixes.some((pre) => p === pre || p.startsWith(`${pre}/`));
}

export function isPortalManufacturerScopedUser(user: MenuGrantUser): boolean {
	const raw = user?.user?.portal_manufacturer_ids;
	if (!Array.isArray(raw)) {
		return false;
	}
	return raw.some((id) => {
		const n = typeof id === 'number' ? id : parseInt(String(id), 10);
		return Number.isFinite(n) && n > 0;
	});
}

/** True when this login is a Moov B2B / brand-portal user and `path` is distributor-only (e.g. Metrc). */
export function portalScopeBlocksPath(path: string, user: MenuGrantUser): boolean {
	return isPortalManufacturerScopedUser(user) && pathMatchesAnyPrefix(path, PORTAL_SCOPED_DENIED_PREFIXES);
}

/**
 * Whether the user may open `path` (e.g. `/metrc/dashboard`), matching sidebar / command-palette rules:
 * Moov B2B portal scope restrictions, then fullaccess at or above {@link FULL_NAV_LEVEL}, storefront exception,
 * or explicit/implied menu grants.
 */
export function userMayAccessPath(
	path: string,
	user: MenuGrantUser,
	options?: { storefrontPaths?: boolean }
): boolean {
	if (!user?.user) {
		return false;
	}
	if (portalScopeBlocksPath(path, user)) {
		return false;
	}
	if (userLevelAtLeast(user, FULL_NAV_LEVEL)) {
		return true;
	}
	const access_permissions = user.user.access_permissions;
	if (options?.storefrontPaths !== false && access_permissions?.length) {
		if (path === '/storefront' || path.startsWith('/storefront/')) {
			if (access_permissions.includes('/system/storefront-settings')) {
				return true;
			}
		}
	}
	return menuPathExplicitOrImplied(path, access_permissions);
}

/** True if `path` is listed in access_permissions, or implied by {@link IMPLIED_MENU_PATHS}. */
export function menuPathExplicitOrImplied(path: string, access_permissions: string[] | null | undefined): boolean {
	if (!access_permissions?.length) {
		return false;
	}
	if (access_permissions.includes(path)) {
		return true;
	}
	// Prefix match for dynamic routes: a stored permission of /metrc/pick grants /metrc/pick/12345.
	// Skip this check for paths that are explicitly declared in the nav (KNOWN_MENU_PATHS) — those
	// must be granted individually.  Without this guard, storing "/customers" would accidentally
	// grant "/customers/dashboard", "/customers/reward-points", etc. via the prefix.
	if (!KNOWN_MENU_PATHS.has(path) &&
		access_permissions.some((p) => typeof p === 'string' && path.startsWith(p + '/'))) {
		return true;
	}
	const impliedBy = IMPLIED_MENU_PATHS[path];
	if (impliedBy?.some((p) => access_permissions.includes(p))) {
		return true;
	}
	if (impliedGeneralLedgerParent(path, access_permissions)) {
		return true;
	}
	if (impliedGeneralLedgerGlImpact(path, access_permissions)) {
		return true;
	}
	if (impliedGeneralLedgerMaintenanceLeaves(path, access_permissions)) {
		return true;
	}
	return false;
}

export type MenuGrantUser = {
	user?: {
		fullaccess?: string | number;
		access_permissions?: string[];
		/** Moov B2B: non-empty when user is limited to brands in `salesman_manufacturer`. */
		portal_manufacturer_ids?: unknown;
	};
} | null;

/**
 * Whether the editor may assign this menu path to another group's access (aligns with sidebar visibility rules).
 */
export function pathGrantableByEditor(path: string, user: MenuGrantUser): boolean {
	if (!user?.user) {
		return false;
	}
	if (portalScopeBlocksPath(path, user)) {
		return false;
	}
	if (userLevelAtLeast(user, SUPER_ADMIN_LEVEL)) {
		return true;
	}
	if (userLevelAtLeast(user, FULL_NAV_LEVEL)) {
		return true;
	}
	const access_permissions = user.user.access_permissions;
	if (access_permissions?.length && (path === '/storefront' || path.startsWith('/storefront/'))) {
		if (access_permissions.includes('/system/storefront-settings')) {
			return true;
		}
	}
	return menuPathExplicitOrImplied(path, access_permissions);
}
